import { PageControls, usePagination } from '@/components/shared/pagination/Pagination';
import tableStrings from '@/components/shared/table/table.translations';
import { TaskType } from '@/services/tasks/task.types';
import { useGetTaskIcon } from '@/services/tasks/useTasks';
import { FC, FunctionComponent } from 'react';
import { FiEdit } from 'react-icons/fi';
import strings from '../admin.translations';

const columnWidths = {
  edit: '16%',
  icon: '16%'
};
const pageSize = 20;

const TasksTableHeader: FC = () => (
  <thead>
    <tr>
      <th className="px-6 pb-2 pt-4 text-left text-xs font-medium tracking-wider text-secondary-std">
        {strings.tasksSection.table.name}
      </th>
      <th className="px-6 pb-2 pt-4 text-left text-xs font-medium tracking-wider text-secondary-std">
        {strings.tasksSection.table.icon}
      </th>
      <th className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std">
        {strings.tasksSection.table.skillsNeeded}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.edit }}
      >
        {strings.tasksSection.table.edit}
      </th>
    </tr>
  </thead>
);

interface TasksTableRowProps {
  task: TaskType;
  index: number;
  onEdit: (skillId: string) => void;
}

const AdminTasksTableRow: FC<TasksTableRowProps> = ({ task, index, onEdit }) => {
  const getTaskIcon = useGetTaskIcon();

  const Icon = getTaskIcon(task.id);

  const alternateRowStyling = index % 2 === 0 ? 'bg-secondary-std' : 'bg-primary-std';
  const disabledRowStyling = 'bg-gray-300';

  return (
    <tr className={task.isActive ? alternateRowStyling : disabledRowStyling}>
      <td className="px-4 py-4 text-sm text-primary-std sm:px-6">
        <div className={`flex h-[2.2rem] flex-col justify-center`}>
          <div>
            <p className="font-semibold">{task.name}</p>
            <p>{task.displayName}</p>
            {!task.isActive && <p>{strings.tasksSection.table.disabled}</p>}
          </div>
        </div>
      </td>
      <td className="px-4 py-4 text-sm font-medium text-primary-std sm:px-6" style={{ width: columnWidths.icon }}>
        <div className={`flex h-[2.2rem] flex-col justify-center`}>
          <div
            className="w-fit p-1 rounded-md"
            style={{
              background: task.isActive ? task.color : undefined,
              color: task.isActive ? task.textColor : '#000'
            }}
          >
            {Icon ? <Icon className="h-6 w-6" /> : <div className="h-6 w-6" />}
          </div>
        </div>
      </td>
      <td className="px-4 py-4 text-sm font-medium text-primary-std sm:px-6">
        <div className={`flex h-[2.2rem] flex-col justify-center`}>
          <div className="text-center">{task.skills.length}</div>
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.edit }}>
        <div className="flex justify-center">
          <button
            type="button"
            aria-label={`${strings.tasksSection.table.edit} ${task.name}`}
            className="text-secondary-std hover:text-primary-std"
            onClick={() => onEdit(task.id)}
          >
            <FiEdit className="h-5 w-5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

type AdminTasksTableProps = {
  tasks: TaskType[];
  onEdit: (taskId: string) => void;
};

const AdminTasksTable: FunctionComponent<AdminTasksTableProps> = ({ tasks, onEdit }) => {
  const [offset, limit, controls] = usePagination(pageSize, tasks.length);
  const paginatedTasks = tasks.slice(offset, offset + limit);

  return (
    <div className="w-full overflow-hidden">
      <table className="min-w-full table-fixed divide-y divide-primary-std border border-primary-std">
        <TasksTableHeader />
        <tbody className="divide-y divide-primary-std">
          {paginatedTasks.map((task, index) => {
            return <AdminTasksTableRow key={task.id} task={task} index={index} onEdit={onEdit} />;
          })}
        </tbody>
      </table>
      {tasks.length > 0 ? (
        <PageControls {...controls} />
      ) : (
        <div className="flex w-full justify-center border-t border-primary-std px-6 py-5">
          <p className="py-1 text-xs font-medium uppercase text-primary-std">{tableStrings.nothingToShow}</p>
        </div>
      )}
    </div>
  );
};

export default AdminTasksTable;
